/**
 * B-24b WS-3 — Account Health / Renewal board panel.
 *
 * Wired to GET /admin/sa-dashboard/account-health.
 * ARR + renewal date come from tenant_contract (NOT Stripe Subscription).
 *
 * Low-data:
 *   - contract_value_cents: null → "no contract on file" (never blank)
 *   - renewal_date: null → "—" (never blank)
 *   - Empty tenants → "No customers yet" message
 *
 * Each row is clickable to open the per-tenant drill.
 */
import { useSaAccountHealth } from "@/hooks/queries/admin/useSaDashboard";
import type { AccountHealthTenant } from "@/lib/saDashboardApi";
import { fmtDate } from "@/lib/relativeTime";
import { OperatorPanel } from "./OperatorPanel";

interface Props {
  onOpenDrill: (tenantId: string, name: string) => void;
}

const CHURN_STYLES: Record<AccountHealthTenant["churn_risk"], string> = {
  low: "text-brand-positive",
  medium: "text-brand-warn",
  high: "text-brand-negative",
  unknown: "text-text-tertiary",
};

function fmtContractValue(cents: number | null): string {
  if (cents === null) return "no contract";
  if (cents === 0) return "$0";
  const d = cents / 100;
  if (d >= 1_000_000) return `$${(d / 1_000_000).toFixed(1)}M`;
  if (d >= 1_000) return `$${(d / 1_000).toFixed(0)}k`;
  return `$${d.toLocaleString()}`;
}

function TenantRow({
  tenant,
  onOpen,
}: {
  tenant: AccountHealthTenant;
  onOpen: (id: string, name: string) => void;
}) {
  return (
    <button
      type="button"
      onClick={() => onOpen(tenant.tenant_id, tenant.name)}
      className="grid w-full grid-cols-[1fr_80px_80px_64px] items-center gap-2 rounded-md px-1 py-2 text-left transition hover:bg-surface-card-hover"
    >
      <span className="min-w-0">
        <span className="block truncate text-xs font-medium text-text-primary">
          {tenant.name}
        </span>
        <span className="block text-[11px] capitalize text-text-tertiary">
          {tenant.status}
        </span>
      </span>
      <span className="text-right font-mono text-[11px] tabular-nums text-text-secondary">
        {fmtContractValue(tenant.contract_value_cents)}
      </span>
      <span className="text-right font-mono text-[11px] tabular-nums text-text-secondary">
        {fmtDate(tenant.renewal_date)}
      </span>
      <span
        className={
          "text-right text-[11px] font-semibold capitalize " +
          CHURN_STYLES[tenant.churn_risk]
        }
      >
        {tenant.churn_risk}
      </span>
    </button>
  );
}

export function AccountHealthPanel({ onOpenDrill }: Props) {
  const { data, isLoading, isError } = useSaAccountHealth();

  return (
    <OperatorPanel
      testId="account-health-panel"
      kicker="Renewals"
      title="Account health"
    >
      {/* Column headers */}
      <div className="grid grid-cols-[1fr_80px_80px_64px] gap-2 border-b border-border-default pb-1.5">
        {["Customer", "Contract", "Renews", "Churn risk"].map((h) => (
          <span
            key={h}
            className={
              "text-right text-[10px] font-bold uppercase tracking-[0.1em] text-text-tertiary first:text-left"
            }
          >
            {h}
          </span>
        ))}
      </div>

      <div className="mt-1">
        {isLoading && (
          <div className="space-y-2 pt-2">
            {[1, 2, 3].map((i) => (
              <div
                key={i}
                className="h-9 animate-pulse rounded bg-surface-canvas"
              />
            ))}
          </div>
        )}
        {isError && (
          <p className="mt-2 text-xs text-brand-negative">
            Couldn't load account health.
          </p>
        )}
        {data && data.tenants.length === 0 && (
          <p
            data-testid="account-health-empty"
            className="mt-2 text-xs text-text-tertiary"
          >
            No customers yet — account health will appear here.
          </p>
        )}
        {data && data.tenants.length > 0 && (
          <div className="max-h-[260px] space-y-px overflow-y-auto">
            {data.tenants.map((t) => (
              <TenantRow key={t.tenant_id} tenant={t} onOpen={onOpenDrill} />
            ))}
          </div>
        )}
      </div>
    </OperatorPanel>
  );
}
